import React, { useState, useEffect, useRef } from "react";
import "./Price.css";

import Title from "../../shared/ui/Title/title";
import ProductCard from "../../shared/ui/ProductCard/ProductCard";

import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

function Price({ setProduct }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("cakes");
  const [isLoading, setIsLoading] = useState(true);
  
  // Состояния сортировки
  const [sortOrder, setSortOrder] = useState("default"); // default, asc (дешевые), desc (дорогие)
  const [isSortOpen, setIsSortOpen] = useState(false); // Состояние открытого кастомного меню
  
  const categoryRefs = useRef({});
  const sortRef = useRef(null); // Ref для закрытия меню по клику вне

  // Опции сортировки
  const sortOptions = [
    { value: 'default', label: 'По умолчанию' },
    { value: 'popular', label: 'Сначала популярные' },
    { value: 'asc', label: 'Сначала дешевле' },
    { value: 'desc', label: 'Сначала дороже' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const productsSnapshot = await getDocs(collection(db, "products"));
        const productsData = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);

        const categoriesSnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = categoriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        const sortedCategories = categoriesData.sort((a, b) => {
          if (a.slug === 'hit') return -1;
          if (b.slug === 'hit') return 1;
          if (a.slug === 'soon') return -1;
          if (b.slug === 'soon') return 1;
          return 0;
        });
        
        setCategories(sortedCategories);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Автоскролл к активной категории
  useEffect(() => {
    if (categoryRefs.current[selectedCategory]) {
      categoryRefs.current[selectedCategory].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedCategory]);

  // Закрытие меню сортировки при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryChange = (slug) => {
    setSelectedCategory(slug);
  };

  // 1. Фильтрация только по выбранной категории
  const filteredProducts = products.filter((product) => {
    return product.category === selectedCategory;
  });

  // 2. Сортировка отфильтрованных товаров
  const sortedAndFilteredProducts = [...filteredProducts].sort((a, b) => {
    if (sortOrder === "asc") return Number(a.price) - Number(b.price);
    if (sortOrder === "desc") return Number(b.price) - Number(a.price);
    if (sortOrder === "popular") return (Number(b.likes) || 0) - (Number(a.likes) || 0);
    return 0; // default
  });

  const getExtras = () => {
    switch(selectedCategory) {
      case 'cakes':
      case 'bento':
      case 'bento-maxi':
      case 'medovik':
        return (
          <section className="extras-section">
            <div className="extras-header">
              <span className="extras-icon">🎨</span>
              <h3 className="extras-title">Дополнительный декор</h3>
            </div>
            <ul className="extras-list">
              <li className="extras-item">
                <span>Шоколадный велюр</span>
                <span className="extras-price">500 ₽</span>
              </li>
              <li className="extras-item">
                <span>Печать на съедобной бумаге</span>
                <span className="extras-price">от 300 ₽</span>
              </li>
              <li className="extras-item">
                <span>Роспись и мазки</span>
                <span className="extras-price">от 100 ₽</span>
              </li>
              <li className="extras-item">
                <span>Кремовый рисунок</span>
                <span className="extras-price">от 200 ₽</span>
              </li>
              <li className="extras-item free-item">
                <span>Кремовая надпись</span>
                <span className="extras-price free">бесплатно</span>
              </li>
            </ul>
          </section>
        );
      
      case 'moti':
        return (
          <section className="extras-section info">
            <div className="extras-header">
              <span className="extras-icon">ℹ️</span>
              <h3 className="extras-title">Информация о моти</h3>
            </div>
            <ul className="extras-list">
              <li className="extras-item">
                <span>Наличие необходимо уточнять</span>
              </li>
              <li className="extras-item">
                <span>Срок изготовления 1-2 дня</span>
              </li>
              <li className="extras-item">
                <span>Возможен любой вкус под заказ</span>
              </li>
            </ul>
          </section>
        );
      
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="price" id="Price">
        <Title title="Меню" translate="Price" />
        <div className="price-loader">
          <div className="loader-spinner"></div>
          <p>Загружаем меню...</p>
        </div>
      </div>
    );
  }

  // Находим текущий лейбл для кнопки сортировки
  const currentSortLabel = sortOptions.find(opt => opt.value === sortOrder)?.label;

  return (
    <div className="price" id="Price">
      <Title title="Меню" translate="Price" />
      
      <div className="price-container">
        
        {/* Чистый и современный блок категорий и сортировки */}
        <div className="menu-controls">
          <div className="categories-scroll">
            {categories.map((category) => (
              <button
                key={category.id}
                ref={el => categoryRefs.current[category.slug] = el}
                className={`category-btn ${selectedCategory === category.slug ? 'active' : ''} 
                          ${category.slug === 'hit' ? 'hit' : ''} 
                          ${category.slug === 'soon' ? 'soon' : ''}`}
                onClick={() => handleCategoryChange(category.slug)}
              >
                {category.slug === 'hit' && <span className="btn-icon">🔥</span>}
                {category.slug === 'soon' && <span className="btn-icon">🚀</span>}
                <span className="btn-text">{category.name}</span>
              </button>
            ))}
          </div>

          {/* КАСТОМНЫЙ ВЫПАДАЮЩИЙ СПИСОК СОРТИРОВКИ */}
          <div className="custom-sort-container" ref={sortRef}>
            <button 
              className={`custom-sort-trigger ${isSortOpen ? 'active' : ''}`}
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <span className="sort-icon">⇅</span>
              <span className="sort-label">{currentSortLabel}</span>
              <span className={`sort-arrow ${isSortOpen ? 'up' : 'down'}`}>▼</span>
            </button>

            {isSortOpen && (
              <div className="custom-sort-dropdown">
                {sortOptions.map(option => (
                  <div 
                    key={option.value}
                    className={`custom-sort-option ${sortOrder === option.value ? 'selected' : ''}`}
                    onClick={() => {
                      setSortOrder(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                    {sortOrder === option.value && <span className="check-icon">✓</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Сетка товаров */}
        <div className="products-grid">
          {sortedAndFilteredProducts.length > 0 ? (
            sortedAndFilteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                id={product.id}
                name={product.name}
                description={product.description}
                old_price={product.old_price}
                price={product.price}
                image={product.image}
                size={product.size}
                gramm={product.gramm}
                setProduct={setProduct}
                category={product.category}
                isHit={product.isHit}
                isNew={product.isNew}
                collectionName="products"
                likes={product.likes}
              />
            ))
          ) : (
            <div className="no-products">
              <span className="no-products-icon">😕</span>
              <p>В этой категории пока нет товаров</p>
            </div>
          )}
        </div>

        {/* Дополнительные опции */}
        {getExtras() && (
          <div className="extras-wrapper">
            {getExtras()}
          </div>
        )}
      </div>
    </div>
  );
}

export default Price;